import { pathOr, values } from 'ramda';
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';

import instance from '../../../../api/instance';
import {
  readOnlyAccessToggle,
  removeAllowedSee,
  setAllowedSee,
  watchPersonAllowedSee
} from '../../../../shared/db/general-db';
import { iDeviceDetails, iFullStoreState, iItemsAllowed, ItemType } from '../../../../shared/interfaces';
import DashboardBlock from '../../../DashboardBlock';
import StandardItem from '../../../general/standard-item';
import { GoBack } from '../../../menus/modal/modal-header';
import { IPropsFromStore, itemStyle, StateInterface } from './PersonCanSee.types';

import styles from './PersonCanSee.module.scss';

import PerfectScrollbar from "react-perfect-scrollbar";
import { ReactComponent as ArrowBackSvg } from "../../../../assets/svg/arrow_back.svg";
import { ReactComponent as CloseTagInfoIcon } from '../../../../assets/svg/close_tag_msg.svg';
import { ReactComponent as HelpIcon } from "../../../../assets/svg/help_24px.svg";
import { ReactComponent as InfoIcon } from "../../../../assets/svg/info_24px.svg";
import { compareStringsAlphabetically } from "../../../../shared/helpers";
import SearchInput from "../../../core/SearchInput";
import SelectList from "../../../elements/SelectList";
import ToggleSwitch from "../../../elements/ToggleSwitch";

const mapStateToProps = (s: iFullStoreState, o: any): IPropsFromStore => {
  return {
    person: s.general.people[o.match.params.id],
    tags: s.general.tags,
    devicesDetails: s.devicesData.devicesDetails,
    authUser: s.auth.user!
  }
}

export const sortTagAndDeviceItems = (items) => {
  return items.sort((a, b) => {
    const nameA = a.props.displayName;
    const nameB = b.props.displayName;

    return compareStringsAlphabetically(nameA, nameB);
  });
};

const PersonCanSeeTest = (props) => {
  const [tagFilter, setTagFilter] = useState<string>('');
  const [deviceFilter, setDeviceFilter] = useState<string>('');
  const [readOnlyAccess, setReadOnlyAccess] = useState<{[key: string]: boolean}>();
  const [allowed, setAllowed] = useState<iItemsAllowed>();
  const [isSelectedAll, setIsSelectedAll] = useState<boolean>(false);
  const [visibleDevices, setVisibleDevices] = useState<any>([]);
  const [showHelpMessage, setShowHelpMessage] = useState<boolean>(false);
  const [hoveredDeviceId, setHoveredDeviceId] = useState<string | null>(null);

  const {person, devicesDetails, tags} = props;
  const devicesIds = devicesDetails.keySeq();

  let canceller;
  const helpMsg = `
        Manage which devices a user can view. Adding a tag will allow this person to see all devices that have the associated tag.
        To prevent a user from seeing a device make sure device overrides and appropriate tags are removed. The complete list
        of seeable devices is at the very bottom.
     `;

  useEffect(() => {
    const {person} = props;

    canceller = watchPersonAllowedSee(person.id, (allowed) => {
      setAllowed(allowed);

      const devicesAccess = allowed?.device
        ? Object.keys(allowed.device).reduce((acc, rec) => {
          return { ...acc, [rec]: allowed.device[rec].readOnly || false };
        }, {})
        : {};

      setReadOnlyAccess({...devicesAccess});
    })

    return () => {
      if (canceller) canceller();
    }
  }, []);

  useEffect(() => {
    if (allowed) {
      const newVisibleDevices = Object.keys(allowed.device || {})
        .filter((deviceId) => devicesIds.includes(deviceId))
        .map((deviceId) => devicesDetails.get(deviceId)!);

      const sortedDevices = newVisibleDevices.sort((a, b) => {
        const nameA = a.name;
        const nameB = b.name;

        return compareStringsAlphabetically(nameA, nameB);
      })

      setVisibleDevices(sortedDevices);
    }
  }, [allowed]);

  const toggleTagFlag = async (tagId) => {
    const {tags, person} = props;

    const isRemove = pathOr(false, [
      tagId,
      'instances',
      'allowed-see',
      'person',
      person.id,
    ])(tags);

    await (isRemove ? removeAllowedSee : setAllowedSee)(
      props.authUser
    )(props.person.id, ItemType.tag, tagId);

    instance.post('/api-firebase/revoke', {uid: props.person.id});
  }

  const toggleDeviceFlag = async (deviceId, tagId = null) => {
    const isRemove = pathOr(false, ['device', deviceId, deviceId])(allowed);

    await (isRemove ? removeAllowedSee : setAllowedSee)(
      props.authUser
    )(props.person.id, ItemType.device, deviceId);

    instance.post('/api-firebase/revoke', {uid: props.person.id});
  }

  const filterChange = (key: keyof StateInterface) => (str) => {
    if(key === 'tagFilter') setTagFilter(str);
    else if(key === 'deviceFilter') setDeviceFilter(str);
  }

  const getSource = (deviceId): {
    id?: string,
    tags?: string[],
    device?: boolean,
    msg: string
  } => {
    if (allowed && allowed.device) {
      const source = Object.entries(allowed.device[deviceId]).filter(source => typeof(source[1]) !== 'boolean');

      const tagSources = source.map(source => {
        if (source[1] === 'tag') return tags[source[0]].details.name
      });
      const isDeviceSource = source.filter(source => source[1] === 'device').length > 0;

      const sources = [];
      if (isDeviceSource) sources.push('device');
      if (tagSources.length > 0) sources.push('tags');

      return {
        id: deviceId,
        tags: tagSources,
        device: isDeviceSource,
        msg: sources.join(' & ')
      }
    }

    return {
      msg: ''
    }
  }

  const unsortedDeviceItems = devicesDetails
    .map((device, deviceId) => (
      <StandardItem
        key={deviceId}
        style={itemStyle(
          pathOr(false, ['device', deviceId, deviceId])(allowed)
        )}
        displayName={device.name}
        itemId={deviceId}
      />
    ))
    .valueSeq()
    .toArray();

  const unsortedTagItems = values(tags).map((tag) => (
    <StandardItem
      style={itemStyle(
        pathOr(false, ['instances', 'allowed-see', 'person', person.id])(
          tag
        )
      )}
      key={tag.details.id}
      displayName={tag.details.name}
      itemId={tag.details.id}
    />
  ));

  const deviceItems = sortTagAndDeviceItems(unsortedDeviceItems);
  const tagItems = sortTagAndDeviceItems(unsortedTagItems);

  const searchTagItems = tagItems.filter(({ props }) =>
    props.displayName?.toLowerCase().includes(tagFilter.toLowerCase())
  );

  const searchDeviceItems = deviceItems.filter(({ props }) =>
    props.displayName?.toLowerCase().includes(deviceFilter.toLowerCase())
  );

  const selectTagItems = tagFilter === ''
    ? tagItems.map(item => {
      const allowedToSee = pathOr(false, ['instances', 'allowed-see', 'person', person.id])(tags[item.key]);

      return {
        id: item.key,
        name: item.props.displayName,
        selected: allowedToSee
      }
    })
    : searchTagItems.map(item => {
      const allowedToSee = pathOr(false, ['instances', 'allowed-see', 'person', person.id])(tags[item.key]);

      return {
        id: item.key,
        name: item.props.displayName,
        selected: allowedToSee
      }
    })

  const selectDeviceItems = deviceFilter === ''
    ? deviceItems.map(item => {
      const allowedToSee = pathOr(false, ['device', item.key, item.key])(allowed);

      return {
        id: item.key,
        name: item.props.displayName,
        selected: allowedToSee
      }
    })
    : searchDeviceItems.map(item => {
      const allowedToSee = pathOr(false, ['device', item.key, item.key])(allowed);

      return {
        id: item.key,
        name: item.props.displayName,
        selected: allowedToSee
      }
    })


  const handleSelectAll = () => {
    const visibleDeviceIds = Object.keys(allowed?.device || {})
      .filter((deviceId) => devicesIds.includes(deviceId))
      .map((deviceId) => devicesDetails.get(deviceId)!);

    const devicesWithNewStatus = visibleDeviceIds
      .map((device: iDeviceDetails) => ({[device.id]: !readOnlyAccess?.[device.id]}))
      .reduce((result, obj) => {
        return {...result, ...obj};
      }, {});

    setReadOnlyAccess({...readOnlyAccess, ...devicesWithNewStatus});

    visibleDeviceIds.map(device => {
      readOnlyAccessToggle(props.authUser)(
        props.person.id,
        device.id,
        !isSelectedAll
      )
    });

    setIsSelectedAll(!isSelectedAll);

    return;
  }

  return (
    <DashboardBlock>
      <div className={styles.PersonVisibleDevices}>
        <div className={styles.PersonVisibleDevicesHeader}>
          <GoBack>
            <ArrowBackSvg style={{position: 'absolute', top: 0, left: 0, color: '#FF7C02'}}/>
          </GoBack>
          {person ? person.displayName : ''}
          <HelpIcon style={{cursor: 'pointer'}} onClick={() => setShowHelpMessage(!showHelpMessage)}/>
        </div>
        {showHelpMessage && <div className={styles.PersonVisibleDevicesHelpMessage}>
            <div className={styles.PersonVisibleDevicesHelpMessageHeader}>
                <div className={styles.PersonVisibleDevicesHelpMessageHeaderLabel}>
                    <InfoIcon style={{height: '16px', width: '16px'}}/>
                    <span>Manage User Device Access</span>
                </div>
                <CloseTagInfoIcon style={{height: '16px', width: '16px', cursor: 'pointer'}} onClick={() => setShowHelpMessage(false)}/>
            </div>
            <p className={styles.PersonVisibleDevicesHelpMessageContent}>
              {helpMsg}
            </p>
        </div>}
        {allowed && <div className={styles.PersonVisibleDevicesSearch}>
          <SearchInput
            value={tagFilter}
            onChange={(filter) => setTagFilter(filter)}
            placeholder={'Search Tags'}
          />
          {selectTagItems && selectTagItems.length > 0 && <SelectList
            entries={selectTagItems}
            selectedCount={0}
            clickHandler={toggleTagFlag}
            showSelectAll={false}
            isSelectedAll={false}
          />}
        </div>}
        {allowed && <div className={styles.PersonVisibleDevicesSearch}>
            <SearchInput
                value={deviceFilter}
                onChange={(filter) => setDeviceFilter(filter)}
                placeholder={'Search Vehicles'}
            />
          {selectDeviceItems && selectDeviceItems.length > 0 && <SelectList
              entries={selectDeviceItems}
              selectedCount={0}
              clickHandler={toggleDeviceFlag}
              showSelectAll={false}
              isSelectedAll={false}
          />}
        </div>}
        {allowed && <div className={styles.PersonVisibleDevicesList}>
          <div className={styles.PersonVisibleDevicesListHeader}>
            <span>Visible Vehicles List:</span>
            <div className={styles.PersonVisibleDevicesListHeaderToggleContainer}>

            </div>
          </div>
          <PerfectScrollbar>
            <table className={styles.PersonVisibleDevicesListTable}>
              <thead>
              <tr className={styles.PersonVisibleDevicesListHeaderRow}>
                <th>Vehicle</th>

                <th>Read Only</th>
              </tr>
              </thead>
              <tbody>
              {visibleDevices.map((device: iDeviceDetails) => {
                const source = getSource(device.id);
                const filteredTagsSource = source.tags ? source.tags.filter(tagName => tagName && tagName.length > 0) : [];

                return (
                  <tr key={device.id} className={styles.PersonVisibleDevicesListTableRow}>
                    <td>
                      {device.name}
                    </td>

                    <td>
                      <ToggleSwitch
                        initialState={readOnlyAccess?.[device.id]}
                        id={`toggle-${device.id}`}
                        onChange={
                          () => {
                            setIsSelectedAll(false);
                            readOnlyAccessToggle(props.authUser)(
                              props.person.id,
                              device.id,
                              !readOnlyAccess?.[device.id]
                            )
                          }
                        }
                      />
                    </td>
                  </tr>
                )
              })}
              </tbody>
            </table>
          </PerfectScrollbar>
        </div>}
      </div>
    </DashboardBlock>
  )
}

export default connect(mapStateToProps)(PersonCanSeeTest);
